<template>
  <v-container fluid class="pa-0 pa-sm-4">
    <v-row class="fill-height">
      <!-- Left Column - Notifications List -->
      <v-col cols="12" md="5" lg="4" class="pr-md-0">
        <v-card 
          class="mb-4 notification-list elevation-2" 
          height="calc(100vh - 120px)"
          rounded="lg"
        >
          <v-card-title class="py-4 px-6">
            <div class="d-flex align-center">
              <v-icon color="primary" class="mr-2">mdi-bell</v-icon>
              <span class="text-h6 font-weight-medium">Notifications</span>
              <v-chip 
                class="ml-3" 
                small 
                :color="unreadNotifications.length ? 'primary' : 'grey'"
                label
              >
                {{ unreadNotifications.length }} unread
              </v-chip>
            </div>
            <v-spacer></v-spacer>
            <v-btn
              color="primary"
              text
              @click="markAllAsRead"
              :disabled="!unreadNotifications.length"
              class="text-none"
            >
              <v-icon left small>mdi-check-all</v-icon>
              Mark all read
            </v-btn>
          </v-card-title>

          <v-divider></v-divider>
          <v-card-text class="pa-0" height="100%">
            <v-list dense two-line class="notification-scroll py-0">
            <v-progress-linear
              v-if="loading"
              indeterminate
              color="primary"
              class="mt-0"
            ></v-progress-linear>
              <template v-if="notifications.length">
                <template v-for="(notification, index) in notifications">
                  <v-list-item
                    :key="notification.id + 'ntfs' + Math.random()"
                    @click="selectNotification(notification)"
                    :class="{ 
                      'unread-notification': !notification.seen,
                      'selected-notification': selectedNotification && selectedNotification.id === notification.id
                    }"
                    class="notification-item py-4 px-6"
                    v-ripple
                  >
                    <v-list-item-avatar>
                      <v-icon :color="notification.seen ? 'grey' : 'primary'">
                        {{ notification.seen ? 'mdi-email-open' : 'mdi-email' }}
                      </v-icon>
                    </v-list-item-avatar>

                    <v-list-item-content>
                      <v-list-item-title class="subtitle-1">{{ notification.title }}</v-list-item-title>
                      <v-list-item-subtitle class="text-truncate">
                        {{ notification.message }}
                      </v-list-item-subtitle>
                      <v-list-item-subtitle class="text-caption">
                        <!-- {{ formatDate(notification.timestamp) }} -->
                        {{ notification?.createdAt }}
                      </v-list-item-subtitle>
                    </v-list-item-content>

                    <v-list-item-action>
                      <v-btn
                        icon
                        small
                        @click.stop="deleteNotification(notification)"
                        color="grey"
                      >
                        <v-icon>mdi-delete</v-icon>
                      </v-btn>
                    </v-list-item-action>
                  </v-list-item>
                  <v-divider
                    v-if="index < notifications.length - 1"
                    :key="`divider-${notification.id}` + Math.random()"
                  ></v-divider>
                </template>
              </template>
              <template v-if="!loading && notifications.length === 0">
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title class="text-center grey--text">
                      No notifications
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </template>
              <template v-if="!hasMore && notifications.length > 0">
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-subtitle class="text-center grey--text">
                      No more notifications
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </template>
            </v-list>
            <v-card-actions class="d-flex justify-space-between pa-4">
              <v-btn
                text
                :disabled="page <= 1 || loading"
                @click="loadPreviousPage"
                color="primary"
              >
                <v-icon left>mdi-chevron-left</v-icon>
                Previous
              </v-btn>
              <v-btn
                text
                :disabled="!hasMore || loading"
                @click="loadNextPage"
                color="primary"
              >
                Next
                <v-icon right>mdi-chevron-right</v-icon>
              </v-btn>
            </v-card-actions>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Right Column - Notification Detail -->
      <v-col cols="12" md="7" lg="8" class="pl-md-4">
        <v-card 
          v-if="selectedNotification" 
          height="calc(100vh - 120px)" 
          class="notification-detail elevation-2" 
          rounded="lg"
        >
          <v-card-title class="py-4 px-6 d-flex align-center">
            <v-icon
              :color="selectedNotification.read ? 'grey' : 'primary'"
              class="mr-2"
            >
              {{ selectedNotification.read ? 'mdi-email-open' : 'mdi-email' }}
            </v-icon>
            {{ selectedNotification.title }}
            <v-spacer></v-spacer>
            <v-btn
              icon
              @click="selectedNotification = null"
              class="d-md-none"
            >
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>

          <v-card-subtitle class="pb-0">
            <!-- {{ formatDate(selectedNotification.timestamp) }} -->
            {{ selectedNotification?.createdAt }}
          </v-card-subtitle>

          <v-divider></v-divider>
          <v-card-text class="pa-6">
            <div class="text-body-1">
              {{ selectedNotification.message }}
            </div>
          </v-card-text>

          <v-card-actions class="mt-auto">
            <v-spacer></v-spacer>
            <v-btn
              text
              color="primary"
              @click="markAsRead(selectedNotification)"
              :disabled="selectedNotification.seen"
            >
              Mark as read
            </v-btn>
            <v-btn
              text
              color="error"
              @click="deleteNotification(selectedNotification)"
              :disabled="selectedNotification.seen"
            >
              Delete
            </v-btn>
          </v-card-actions>
        </v-card>

        <v-card 
          v-else 
          height="calc(100vh - 120px)" 
          class="d-none d-md-block notification-detail elevation-2" 
          rounded="lg"
        >
          <v-card-text class="text-center grey--text text-h6 d-flex align-center justify-center" style="height: 100%">
            Select a notification to view details
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { mapGetters } from 'vuex';

import Notification from '@/shared/apis/notification'
import Api from 'main/api';
import ErrorResolver from 'shared/ErrorResolver';

export default {
  name: 'NotificationPage',
  
  data() {
    return {
      selectedNotification: null,
      notifications: [],
      page: 0,
      size: 5,
      loading: false,
      hasMore: true,
      observer: null,
    }
  },

  computed: {
    unreadNotifications() {
      return this.notifications.filter(notification => !notification.read)
    },

    ...mapGetters(['getUser'])
  },

  methods: {
    formatDate(date) {
      return new Date(date).toLocaleString()
    },

    markAsRead(notification) {
      if (!notification.seen) {
        this.loading = true
        Notification.markAsSeen(notification.notificationId, this.getUser?.user?.id ?? this.getUser?.id).then(res => {
          notification.seen = true
          this.loading = false
        }).catch(err => {
          ErrorResolver.checkErrors(this, err)
          this.loading = false
        })
      }
    },

    selectNotification(notification) {
      this.selectedNotification = notification
      this.markAsRead(notification)
    },

    markAllAsRead() {
      this.notifications.forEach(notification => {
        notification.read = true
      })
    },

    deleteNotification(notification) {
      const index = this.notifications.findIndex(n => n.id === notification.id)
      if (index !== -1) {
        this.notifications.splice(index, 1)
      }
    },

    async fetchNotifications(resetPage = false) {
      if (this.loading || !this.getUser) return
      if (resetPage) {
        this.page = 1
        this.hasMore = true
      }

      try {
        if(this.getUser){
          this.loading = true
          const currentPage = this.page > 0 ? this.page - 1 : 0
          Api.notification.fetchAll(currentPage, this.size, this.getUser?.user?.userId ?? this.getUser?.userId).then(res => {
            this.notifications = res.data.content
            this.hasMore = res.data.content.length === this.size
            this.loading = false
          }).catch(err => {
            ErrorResolver.checkErrors(this, err)
            this.loading = false
          })
        }
      } catch (error) {
        console.error('Error fetching notifications:', error)
        this.loading = false
      }
    },

    loadNextPage() {
      if (this.hasMore && !this.loading) {
        this.page++
        this.fetchNotifications()
      }
    },

    loadPreviousPage() {
      if (this.page > 1 && !this.loading) {
        this.page--
        this.fetchNotifications()
      }
    },
    handleIntersect(entries) {
      const entry = entries[0]
      if (entry.isIntersecting) {
        this.fetchNotifications()
      }
    },

    observeLastItem() {
      if (!this.observer) return
      
      // Disconnect previous observations
      this.observer.disconnect()

      // Find the last item element
      const lastItem = this.$el.querySelector('.notification-item:last-child')
      if (lastItem) {
        this.observer.observe(lastItem)
      }
    }
  },

  created() {
    this.fetchNotifications()
  },

  mounted() {
    // Create intersection observer for infinite scroll
    this.observer = new IntersectionObserver((entries) => this.handleIntersect(entries), {
      threshold: 0.5
    })
    // Observe the last item for infinite scroll
    this.$nextTick(() => {
      this.observeLastItem()
    })
  },

  beforeDestroy() {
    // Clean up the observer
    if (this.observer) {
      this.observer.disconnect()
    }
  }
}
</script>

<style scoped>
.notification-list {
  position: relative;
  display: flex;
  flex-direction: column;
  border: none;
  transition: all 0.3s ease;
}

.notification-scroll {
  height: 100%;
  overflow-y: auto;
}

.notification-scroll::-webkit-scrollbar {
  width: 8px;
}

.notification-scroll::-webkit-scrollbar-track {
  background: transparent;
}

.notification-scroll::-webkit-scrollbar-thumb {
  background: #e0e0e0;
  border-radius: 4px;
}

.notification-scroll::-webkit-scrollbar-thumb:hover {
  background: #bdbdbd;
}

.notification-detail {
  display: flex;
  flex-direction: column;
  border: none;
  transition: all 0.3s ease;
}

.notification-item {
  transition: all 0.2s ease;
  border-radius: 8px;
  margin: 4px 8px;
}

.notification-item:hover {
  background-color: #f5f5f5;
}

.unread-notification {
  background-color: #fafafa;
}

.unread-notification::before {
  content: '';
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 4px;
  height: 60%;
  background-color: var(--v-primary-base);
  border-radius: 0 2px 2px 0;
}

.selected-notification {
  background-color: var(--v-primary-lighten4) !important;
}

.v-list-item__subtitle {
  margin-top: 4px;
  line-height: 1.4;
}

.text-body-1 {
  line-height: 1.6;
}

@media (max-width: 959px) {
  .notification-list,
  .notification-detail {
    height: auto !important;
  }
  
  .notification-item {
    margin: 2px 4px;
  }
}
</style>
